"use client";

import { IconShoppingBag } from "@tabler/icons-react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { buttonVariants } from "@/components/ui/button";
import { getNavigationItems } from "@/data/navigation";
import { useCartStore } from "@/features/cart/components";
import { Link } from "@/i18n/navigation";
import { useMobile } from "@/lib/hooks/use-mobile";
import { useScrollPosition } from "@/lib/hooks/use-scroll-position";
import { cn } from "@/lib/utils";
import type { HeaderThemeType } from "@/types/common.types";
import { HeaderAccount } from "./header-account";
import { HeaderAccountMobile } from "./header-account-mobile";
import { HeaderCta } from "./header-cta";
import { HeaderSearchForm } from "./header-search-form";
import { LanguageSwitcher } from "./language";
import { BrandLogo } from "./logo";
import { NavigationDesktop } from "./navigation-desktop";
import { MobileNavigation } from "./navigation-mobile";
import { TopBar } from "./top-bar";

type HeaderMainProps = {
  theme?: HeaderThemeType;
};

type ListingFiltersAttachmentDetail = {
  isAttached: boolean;
};

export const HeaderMain = ({ theme }: HeaderMainProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isListingFiltersAttached, setIsListingFiltersAttached] = useState(false);
  const [innerTheme, setInnerTheme] =
    useState<HeaderMainProps["theme"]>("dark-hero");
  const scrollPosition = useScrollPosition();
  const pathname = usePathname();
  const cartStore = useCartStore((state) => state);
  const { isMobile } = useMobile();



  const t = useTranslations();
  const items = getNavigationItems().map((item) => ({
    ...item,
    label: t(`navigation.${item.label}`),
  }));

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    const handleListingFiltersAttachment = (event: Event) => {
      const customEvent = event as CustomEvent<ListingFiltersAttachmentDetail>;
      setIsListingFiltersAttached(customEvent.detail.isAttached);
    };

    window.addEventListener(
      "listing-mobile-filters-attach-change",
      handleListingFiltersAttachment as EventListener
    );

    return () => {
      window.removeEventListener(
        "listing-mobile-filters-attach-change",
        handleListingFiltersAttachment as EventListener
      );
    };
  }, []);

  useEffect(() => {
    if (theme) {
      const updatedTheme =
        scrollPosition > 1 ? (theme === "all-light" ? theme : "light") : theme;
      setInnerTheme(updatedTheme);
    } else {
      const hero = document.querySelector('[data-hero="true"]');
      const newTheme =
        scrollPosition > 1 ? "light" : hero ? "dark-hero" : "dark";
      setInnerTheme(newTheme);
    }
  }, [scrollPosition, pathname]);
  
  return (
    <div
      className="sticky top-0 z-19 w-full -mb-18.25  md:-mb-24.25"
      data-sticky-main-header="true"
      style={{
        top: "var(--listing-mobile-header-top, 0px)",
        transition: "top 0.3s ease",
      }}
    >
      <TopBar
        containerClassName="gap-4 xl:gap-6"
        forceSolidSurface={isListingFiltersAttached}
        theme={innerTheme}
      >
        <BrandLogo
          dark={innerTheme !== "light" && innerTheme !== "all-light"}
        />
        {pathname !== "/przesiadka" && (
          <HeaderSearchForm
          className="ml-auto xl:ml-4"
          dark={innerTheme !== "light" && innerTheme !== "all-light"}
          />
        )}
        {isHydrated && (
          <div className="mr-1 flex items-center lg:hidden">
            <HeaderAccountMobile
              hideSignIn={pathname === "/przesiadka"}
              theme={innerTheme}
            />
          </div>
        )}
        {pathname !== "/przesiadka" && (
          <NavigationDesktop
          className={cn({ "text-primary": innerTheme === "all-light" })}
          items={items}
          />
        )}
        <div className="hidden items-center space-x-4 first-letter:hidden lg:ml-auto lg:flex">
          {isHydrated && (
            <div className="flex items-center">
              <HeaderAccount
                hideSignIn={pathname === "/przesiadka"}
                theme={innerTheme}
              />
            </div>
          )}
          <LanguageSwitcher
            className="lg:px-2"
            triggerClassName={
              innerTheme === "all-light" || innerTheme === "light"
                ? "text-primary"
                : ""
            }
          />
          {pathname !== "/przesiadka" && (
            <HeaderCta innerTheme={innerTheme} />
          )}
        </div>

        {pathname !== "/przesiadka" && (
          <>
        <Link
          className={cn(
            buttonVariants({
              variant: "ghost",
              size: "icon",
            }),
            "h-10 whitespace-nowrap transition-none lg:hidden"
          )}
          data-pw="mobile-cart-button"
          href="/cart"
          title={t("header.cart")}
          >
          <div className="relative">
            <IconShoppingBag
              className={cn(
                (innerTheme === "light" || innerTheme === "all-light") &&
                "text-primary",
                "size-7"
              )}
              stroke={1.5}
              />
            {cartStore.items.length > 0 && (
              <span
              className={cn(
                "-right-0.5 -top-0.5 absolute flex h-3.5 w-3.5 items-center justify-center rounded-full text-[10px] leading-none transition-colors duration-200",
                scrollPosition > 1
                ? "bg-brand-yellow"
                : innerTheme === "light" ||
                innerTheme === "dark-hero" ||
                innerTheme === "all-light"
                ? "bg-brand-green2-300 text-white"
                : "bg-brand-yellow text-brand-green2-300"
              )}
              >
                {cartStore.items.length}
              </span>
            )}
          </div>
        </Link>
          <MobileNavigation
          className="lg:hidden"
          theme={innerTheme}
          />
          </>
        )}
      </TopBar>
    </div>
  );
};
